import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBag,
  CreditCard,
  Repeat,
  ArrowRight,
  Check,
  X,
  Flame,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const options = [
  {
    id: "achat",
    icon: ShoppingBag,
    title: "Achat",
    subtitle: "Devenez propriétaire de votre site",
    price: "1490€",
    priceTax: "TTC",
    priceLabel: "À partir de",
    tagline:
      "Un site professionnel conçu pour générer vos premiers clients.",
    ctaLabel: "Lancer mon projet",
    highlight: false,
    features: [
      "Site personnalisé adapté à l'activité",
      "Design impactant",
      "Optimisé pour la conversion",
      "Livraison clé en main",
      "Évolutif",
      "Accompagnement personnalisé",
      "Conseils business",
    ],
    description:
      "Solution idéale pour les entreprises souhaitant un site professionnel sans engagement.",
  },
  {
    id: "paiement",
    icon: CreditCard,
    title: "Paiement en plusieurs fois",
    subtitle: "Étalez votre investissement",
    price: "3x 550€",
    priceTax: "TTC",
    priceLabel: "À partir de",
    tagline: "Lancez votre site sans impacter votre trésorerie.",
    ctaLabel: "Lancer mon projet",
    highlight: false,
    features: [
      "Même prestation que l'achat",
      "Paiement fractionné en 3 fois",
      "Accès au site dès le premier paiement",
      "Site personnalisé adapté à l'activité",
      "Optimisé pour la conversion",
      "Accompagnement personnalisé",
      "Conseils business",
    ],
    description:
      "Idéal pour lancer votre projet en gardant de la flexibilité financière.",
  },
  {
    id: "loueur",
    icon: Repeat,
    title: "Offre Loueur",
    subtitle: "Site + accompagnement pour activité de location",
    price: "180€",
    priceSuffix: "/mois",
    priceTax: "TTC",
    priceLabel: "À partir de",
    tagline: "Lancez votre activité de location sans investissement initial.",
    ctaLabel: "Demander un devis",
    isSubscription: false,
    highlight: true,
    badge: "Le plus choisi",
    features: [
      "Création complète du site incluse",
      "Site personnalisé pour la location",
      "Maintenance et mises à jour",
      "Hébergement inclus",
      "Accompagnement continu",
      "Optimisation régulière",
      "Conseils business",
    ],
    notice: "Offre sur devis · Adaptée à votre activité",
    description:
      "La formule tout-inclus pour démarrer fort une activité de location et être accompagné dans la durée. Devis personnalisé selon vos besoins.",
  },
];

export default function WebSection({ onOpenQuote }) {
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(false);
  const active = options.find((o) => o.id === activeId) || null;
  const bgImage =
    "https://static.prod-images.emergentagent.com/jobs/f60ae1e6-6bb5-4834-a099-1fb4ea0ab263/images/aadc640b1605a9ae7542ed811e22bc68290ffbd8e87c1cee00408aa6d7572ffc.png";

  const handleCTA = async () => {
    if (active?.isSubscription && active?.planId) {
      setLoading(true);
      try {
        const res = await axios.post(`${API}/checkout/subscription`, {
          plan_id: active.planId,
          origin_url: window.location.origin,
        });
        if (res.data?.url) {
          window.location.href = res.data.url;
          return;
        }
        toast.error("Erreur de redirection vers le paiement.");
      } catch (e) {
        toast.error("Impossible de démarrer le paiement. Merci de réessayer.");
      } finally {
        setLoading(false);
      }
      return;
    }
    setActiveId(null);
    setTimeout(() => onOpenQuote(), 150);
  };

  return (
    <section
      id="sites-web"
      data-testid="websection"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{ backgroundImage: `url(${bgImage})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050507] via-[#050507]/90 to-[#050507]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_20%,rgba(31,44,110,0.22),transparent_55%),radial-gradient(ellipse_at_20%_80%,rgba(229,36,49,0.12),transparent_55%)]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-14 md:mb-20"
        >
          <span className="text-xs uppercase tracking-[0.25em] text-[#1F2C6E] brightness-150">
            — Sites web
          </span>
          <h2 className="mt-4 font-outfit text-4xl md:text-5xl lg:text-6xl font-light tracking-tight">
            Création de sites web{" "}
            <span className="gradient-text font-medium">professionnels</span>
          </h2>
          <p className="mt-6 text-white/65 text-lg font-light leading-relaxed max-w-2xl">
            Trois formules pour s&rsquo;adapter à votre situation. Cliquez sur
            une carte pour découvrir le détail.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {options.map((opt, i) => {
            const Icon = opt.icon;
            return (
              <motion.button
                key={opt.id}
                type="button"
                onClick={() => setActiveId(opt.id)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                whileHover={{ y: -6 }}
                data-testid={`web-card-${opt.id}`}
                className={`group relative text-left rounded-3xl p-8 md:p-9 border transition-all duration-500 ${
                  opt.highlight
                    ? "bg-gradient-to-b from-[#1a0f12] to-[#141418] border-[#E52431]/50 glow-red"
                    : "bg-[#141418] border-white/10 hover:border-white/25"
                }`}
              >
                {opt.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap">
                    <div className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] gradient-bg text-white font-semibold shadow-[0_8px_20px_rgba(229,36,49,0.35)]">
                      <Flame size={12} /> {opt.badge}
                    </div>
                  </div>
                )}

                <div
                  className={`absolute top-0 left-8 right-8 h-px ${
                    opt.highlight
                      ? "bg-gradient-to-r from-transparent via-[#E52431] to-transparent"
                      : "bg-gradient-to-r from-transparent via-white/15 to-transparent"
                  }`}
                />

                <div className="flex items-start justify-between mb-8">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#E52431]/20 to-[#1F2C6E]/30 border border-white/10 grid place-items-center group-hover:scale-110 transition-transform duration-500">
                    <Icon size={22} className="text-white" strokeWidth={1.5} />
                  </div>
                  <ArrowRight
                    size={18}
                    className="text-white/30 group-hover:text-white group-hover:translate-x-1 transition-all duration-300"
                  />
                </div>

                <h3 className="font-outfit text-xl md:text-2xl font-medium text-white">
                  {opt.title}
                </h3>
                <p className="mt-2 text-white/55 text-sm font-light min-h-[40px]">
                  {opt.subtitle}
                </p>

                <div className="mt-6 flex items-baseline gap-2">
                  <span className="text-xs text-white/50 uppercase tracking-wider">
                    {opt.priceLabel}
                  </span>
                </div>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="font-outfit text-4xl md:text-5xl font-light tracking-tight">
                    {opt.price}
                  </span>
                  {opt.priceTax && (
                    <span className="text-[10px] font-semibold tracking-[0.15em] text-white/55 ml-0.5">
                      {opt.priceTax}
                    </span>
                  )}
                  {opt.priceSuffix && (
                    <span className="text-xl text-white/70 ml-1">
                      {opt.priceSuffix}
                    </span>
                  )}
                </div>

                <p className="mt-4 text-sm text-white/60 font-light leading-relaxed min-h-[40px]">
                  {opt.tagline}
                </p>

                <div
                  className={`mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold transition-colors ${
                    opt.highlight
                      ? "text-[#E52431]"
                      : "text-white/60 group-hover:text-white"
                  }`}
                >
                  Voir le détail
                  <ArrowRight size={14} />
                </div>
              </motion.button>
            );
          })}
        </div>

        <p className="mt-10 text-center text-sm text-white/45 font-light">
          Chaque projet est conçu sur mesure selon vos objectifs et votre
          activité.
        </p>
        <p className="mt-2 text-center text-xs text-white/35 font-light">
          Tous les prix affichés sont en TTC.
        </p>
      </div>

      {/* Dialog with detail */}
      <Dialog
        open={!!active}
        onOpenChange={(o) => !o && setActiveId(null)}
      >
        <DialogContent
          data-testid="web-detail-dialog"
          className="bg-[#141418] border border-white/10 text-white max-w-xl rounded-3xl overflow-hidden p-0"
        >
          <AnimatePresence mode="wait">
            {active && (
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.3 }}
              >
                {/* Header accent */}
                <div className="relative h-1 gradient-bg" />

                <div className="p-8 md:p-10">
                  <DialogHeader className="text-left">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#E52431]/25 to-[#1F2C6E]/40 border border-white/10 grid place-items-center">
                        <active.icon
                          size={22}
                          className="text-white"
                          strokeWidth={1.5}
                        />
                      </div>
                      {active.highlight && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] gradient-bg text-white font-semibold">
                          <Flame size={11} /> {active.badge}
                        </span>
                      )}
                    </div>

                    <DialogTitle className="font-outfit text-2xl md:text-3xl font-light">
                      {active.title}
                    </DialogTitle>
                    <DialogDescription className="text-white/55 mt-1">
                      {active.subtitle}
                    </DialogDescription>
                  </DialogHeader>

                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="text-xs text-white/50 uppercase tracking-wider">
                      {active.priceLabel}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="font-outfit text-5xl md:text-6xl font-light tracking-tight gradient-text">
                      {active.price}
                    </span>
                    {active.priceTax && (
                      <span className="text-[11px] font-semibold tracking-[0.15em] text-white/60 ml-1">
                        {active.priceTax}
                      </span>
                    )}
                    {active.priceSuffix && (
                      <span className="text-2xl text-white/70 ml-1">
                        {active.priceSuffix}
                      </span>
                    )}
                  </div>

                  <p className="mt-4 text-white/75 font-light leading-relaxed">
                    {active.tagline}
                  </p>

                  <ul className="mt-8 space-y-3">
                    {active.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-3 text-white/80"
                      >
                        <Check
                          size={16}
                          className="mt-1 flex-shrink-0 text-[#E52431]"
                          strokeWidth={2.2}
                        />
                        <span className="font-light">{f}</span>
                      </li>
                    ))}
                  </ul>

                  {active.notice && (
                    <div className="mt-5 px-4 py-3 rounded-xl border border-[#E52431]/30 bg-[#E52431]/10 text-sm text-white/85">
                      ⚠️ {active.notice}
                    </div>
                  )}

                  <p className="mt-6 text-white/60 font-light text-sm leading-relaxed">
                    {active.description}
                  </p>

                  <button
                    data-testid={`web-dialog-cta-${active.id}`}
                    onClick={handleCTA}
                    disabled={loading}
                    className="mt-8 w-full rounded-full gradient-bg text-white font-semibold py-4 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_40px_rgba(229,36,49,0.45)] transition-all duration-300 btn-shine inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? "Redirection..." : active.ctaLabel}
                    {!loading && <ArrowRight size={16} />}
                  </button>
                  <p className="mt-3 text-center text-xs text-white/50 font-light">
                    {active.isSubscription
                      ? "⚡ Paiement sécurisé Stripe · Sans engagement long"
                      : "⚡ Réponse sous 24h"}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </section>
  );
}
